import React from 'react';
import { Layout, Menu, message, Grid } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router';
import { UserOutlined } from '@ant-design/icons';
import { useAuth } from '../providers/AuthProvider';
import { useMutation } from '@apollo/client';
import { LOG_OUT } from '../graphql/mutations/mutations';
import { canBeShowed } from '../utils/utils';
import { IMenuItem } from '../types/menu';
import {
  headerStyle,
  contentStyle,
  mobileContentStyle,
  layoutStyle,
  logoStyle,
} from './LoggedLayout.styles';
import {
  BASE_MENU_ITEMS,
  ADMIN_MENU_ITEM,
  USER_MENU_ITEMS,
  MOBILE_NAV_ITEMS,
  ADMIN_MOBILE_NAV_ITEM,
  PROFILE_MOBILE_NAV_ITEM,
} from '../consts/menu';
import MobileBottomNav from '../components/Navigation/MobileBottomNav';

const { Header, Content } = Layout;
const { useBreakpoint } = Grid;

interface LoggedLayoutProps {
  children: React.ReactNode;
}

export const LoggedLayout: React.FC<LoggedLayoutProps> = ({ children }) => {
  const { user, removeAuthUser } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const location = useLocation();

  const selectedDesktop = location.pathname ? '/' + location.pathname.split('/')[1] : '';
  const selectedMobile = location.pathname || '';

  const [logout] = useMutation(LOG_OUT, {
    onCompleted: data => {
      if (data?.LogOut) {
        removeAuthUser();
        message.success('Logged out successfully');
        navigate('/login');
      }
    },
    onError: error => {
      message.error(error.message || 'Logout failed');
    },
  });

  const buildDesktopMenuItems = (): IMenuItem[] => {
    const items: IMenuItem[] = BASE_MENU_ITEMS.map(item => ({
      ...item,
      label:
        typeof item.label === 'string' ? (
          <Link to={item.key || '/'}>{item.label}</Link>
        ) : (
          item.label
        ),
    }));

    if (user && canBeShowed(user, ['ADMIN'])) {
      items.push({
        ...ADMIN_MENU_ITEM,
        label: <Link to={ADMIN_MENU_ITEM.key || '/admin'}>{ADMIN_MENU_ITEM.label}</Link>,
      });
    }

    if (!isMobile) {
      items.unshift({
        key: 'logo',
        disabled: true,
        style: { pointerEvents: 'none' },
        label: <img src="/public/images/logo.png" alt="menu" style={logoStyle} />,
      });
    }

    const userMenuItems: IMenuItem[] = USER_MENU_ITEMS.map(item => {
      if (item.key === '/logout') {
        return {
          ...item,
          onClick: () => logout(),
        };
      }

      return {
        ...item,
        label:
          typeof item.label === 'string' && item.key !== '/logout' ? (
            <Link to={item.key || '/'}>{item.label}</Link>
          ) : (
            item.label
          ),
      };
    });

    items.push({
      key: 'user-menu',
      style: { marginLeft: 'auto' },
      label: <UserOutlined />,
      children: userMenuItems,
    });

    return items;
  };

  const buildMobileNavItems = () => {
    const items = [...MOBILE_NAV_ITEMS];

    if (user && canBeShowed(user, ['ADMIN'])) {
      items.push(ADMIN_MOBILE_NAV_ITEM);
    }

    items.push(PROFILE_MOBILE_NAV_ITEM);

    return items.map(item => ({
      ...item,
      onClick: () => navigate(item.key),
    }));
  };

  const menuItems = buildDesktopMenuItems();
  const mobileNavItems = buildMobileNavItems();

  return (
    <Layout style={layoutStyle}>
      {!isMobile && (
        <Header style={headerStyle}>
          <Menu
            theme="light"
            mode="horizontal"
            defaultSelectedKeys={['/']}
            selectedKeys={[selectedDesktop]}
            items={menuItems}
          />
        </Header>
      )}

      <Content style={!isMobile ? contentStyle : mobileContentStyle}>{children}</Content>

      {isMobile && (
        <MobileBottomNav
          items={mobileNavItems}
          selectedKey={selectedMobile}
          accentColor="#ee8865"
        />
      )}
    </Layout>
  );
};
